const BASE = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api`;
let csrf = '';
let bootstrapPromise;
let bootstrapInFlight = false;
export class ApiError extends Error {
  constructor(message, status, details) { super(message); this.status = status; this.details = details; }
}
async function perform(path, options = {}) {
  const { timeoutMs = 25000, ...requestOptions } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${BASE}${path}`, { ...requestOptions, credentials: options.credentials || 'include', cache: 'no-store', signal: controller.signal, headers: { ...(options.method && options.method !== 'GET' ? { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf } : {}), ...options.headers } });
    let data;
    try { data = await response.json(); }
    catch {
      // Non-JSON proxy/server errors keep their HTTP status; a malformed success
      // must never masquerade as a saved play or a valid bootstrap response.
      if (response.ok) throw new ApiError('Something went wrong. Please try again.', response.status);
      data = {};
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new ApiError('Something went wrong. Please try again.', response.status);
    if (!response.ok) throw new ApiError(typeof data.detail === 'string' ? data.detail : response.status >= 500 ? 'This service is temporarily unavailable. Please try again; your saved plays are safe.' : 'Please check your details and try again.', response.status, data);
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('We couldn’t reach the server. Your saved results are safe; Endless is still available.', 0);
  } finally { clearTimeout(timer); }
}
export function bootstrap(force = false) {
  if (!bootstrapPromise || (force && !bootstrapInFlight)) {
    bootstrapInFlight = true;
    bootstrapPromise = perform('/bootstrap').then(data => { csrf = data.csrf_token; return data; }).catch(error => { bootstrapPromise = null; throw error; }).finally(() => { bootstrapInFlight = false; });
  }
  return bootstrapPromise;
}
// Cookie-free public reads must never queue behind authentication/bootstrap.
export const publicApi = (path, options = {}) => perform(path, { timeoutMs: 10000, ...options, credentials: 'omit' });
export async function api(path, options = {}) {
  const { isCurrent, ...requestOptions } = options;
  await bootstrap();
  // Recheck after bootstrap's async boundary, immediately before sending cookies.
  if (isCurrent && !isCurrent()) throw new ApiError('Your page needs to reconnect. Please try again.', 0);
  return perform(path, { ...requestOptions, body: options.body === undefined ? undefined : JSON.stringify(options.body) });
}
// Device snapshots are game data, never a session/token store. Only retain the
// public profile fields used for cache ownership and offline nickname display.
function deviceValue(key, value) {
  if (!value || !(key.startsWith('perfect-slice.daily-cache.v1:') || key === 'perfect-slice.daily-last.v1')) return value;
  const user = value.user ? { id: value.user.id, alias: value.user.alias, alias_confirmed: value.user.alias_confirmed } : null;
  return { challenge: value.challenge, state: value.state, user, ...(value.guest ? { guest: { id: value.guest.id, alias: value.guest.alias, created_at: value.guest.created_at } } : {}), ...(value.my_entry ? { my_entry: value.my_entry } : {}) };
}
export function loadLocal(key) { try { return deviceValue(key, JSON.parse(localStorage.getItem(key) || 'null')); } catch { /* Invalid or unavailable optional cache: refetch authoritative data. */ return null; } }
export function saveLocal(key, value) { try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, JSON.stringify(deviceValue(key, value))); } catch { /* Server remains authoritative without local storage. */ } }


// A nickname is display text, never a cache-owner identifier. Keep legacy
// alias-keyed device data readable while moving signed-in caches to the UUID.
export function updateCachedNickname(previous, next) {
  if (!previous?.alias_confirmed || !next?.id || previous.id !== next.id) return;
  try {
    const keys = [`perfect-slice.daily-cache.v1:${previous.id}`, `perfect-slice.daily-cache.v1:${previous.alias}`, 'perfect-slice.daily-last.v1'];
    for (const key of keys) {
      const cached = loadLocal(key);
      const own = cached?.user?.id ? cached.user.id === next.id : cached?.user?.alias === previous.alias;
      if (!own) continue;
      const updated = { ...cached, user: next, my_entry: cached.my_entry ? { ...cached.my_entry, player: next.alias } : null };
      saveLocal(key === 'perfect-slice.daily-last.v1' ? key : `perfect-slice.daily-cache.v1:${next.id}`, updated);
      if (key !== 'perfect-slice.daily-last.v1' && key !== `perfect-slice.daily-cache.v1:${next.id}`) saveLocal(key, null);
    }
  } catch { /* Browser storage never controls account identity or scores. */ }
}
